import Decimal from 'decimal.js'

const ARABIC_INDIC_ZERO = 0x0660 // ٠
const PERSIAN_ZERO = 0x06f0 // ۰

/** Upper bound for user-entered amounts; keeps formatting and layout sane. */
export const MAX_AMOUNT = new Decimal('1e15')

/**
 * Normalizes user-typed numbers to an ASCII decimal string.
 * Accepts Arabic-Indic/Persian digits, the Arabic decimal separator (٫),
 * and strips grouping separators (`,` `٬` spaces).
 */
export function normalizeNumericInput(raw: string): string {
  let out = ''
  for (const ch of raw) {
    const cp = ch.codePointAt(0)!
    if (cp >= ARABIC_INDIC_ZERO && cp <= ARABIC_INDIC_ZERO + 9) out += String(cp - ARABIC_INDIC_ZERO)
    else if (cp >= PERSIAN_ZERO && cp <= PERSIAN_ZERO + 9) out += String(cp - PERSIAN_ZERO)
    else if (ch === '٫') out += '.'
    else if (ch === ',' || ch === '٬' || ch === ' ' || ch === ' ' || ch === '٬') continue
    else out += ch
  }
  return out.trim()
}

/** Keeps only what an amount field may contain while the user is typing. */
export function sanitizeAmountTyping(raw: string): string {
  const normalized = normalizeNumericInput(raw).replace(/[^\d.]/g, '')
  const firstDot = normalized.indexOf('.')
  if (firstDot === -1) return normalized
  return normalized.slice(0, firstDot + 1) + normalized.slice(firstDot + 1).replace(/\./g, '')
}

/** Adds thousands separators to a raw "1234.5" string for display in the amount field. */
export function groupAmount(raw: string): string {
  const [int = '', frac] = raw.split('.')
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return frac === undefined ? grouped : `${grouped}.${frac}`
}

/** Parses a user amount. Returns null for empty, invalid, negative or out-of-range input. */
export function parseAmount(raw: string): Decimal | null {
  const s = normalizeNumericInput(raw)
  if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return null
  const value = new Decimal(s)
  if (value.isNegative() || value.greaterThan(MAX_AMOUNT)) return null
  return value
}
